<script setup lang="ts">

const props = defineProps<{
  locale: string;
  languages: { value: string; label: string }[];
}>();

const emit = defineEmits<{ search: [] }>();

// Shares the `sections` cache — pulse together with HeaderLogo and HeaderNavCenter.
const sections = useSections(computed(() => props.locale));
const { data: settings } = useSettings(computed(() => props.locale));
// See HeaderNavCenter — static, non-animated placeholders during the
// pre-hydration window keep `<e-button>` dimensions reserved so the
// body doesn't shift when WC upgrade settles. No animate-pulse so
// Lighthouse doesn't see continuous activity.
const mounted = useIsMounted();
const loading = computed(() => !mounted.value);

const labels = computed(() => ({
  search: settings.value?.header?.search ?? 'Search',
  change_language: settings.value?.header?.change_language ?? 'Change language',
}));
</script>

<template>
  <div class="hidden md:flex ml-auto items-center gap-2">
    <template v-if="loading">
      <div
        role="presentation"
        aria-hidden="true"
        class="h-9 w-9 rounded-lg bg-(--color-elevation-200)/40"
      />
      <div
        role="presentation"
        aria-hidden="true"
        class="h-6 w-px shrink-0 self-center bg-(--color-elevation-200)"
      />
      <div
        role="presentation"
        aria-hidden="true"
        class="h-9 w-9 rounded-lg bg-(--color-elevation-200)/40"
      />
    </template>
    <template v-else>
      <e-button
        id="btn-search"
        variant="ghost"
        icon="search"
        size="sm"
        square
        :aria-label="labels.search"
        @click="emit('search')"
      />
      <div
        role="presentation"
        aria-hidden="true"
        class="h-6 w-px shrink-0 self-center bg-(--color-elevation-200)"
      />
      <HeaderAuthMenu :locale="locale" :languages="languages" />
      <e-tooltip for="#btn-search" placement="bottom" size="xs" :open-delay="400">
        <span class="flex items-center gap-2">
          {{ labels.search }}
          <span class="flex items-center gap-0.5">
            <e-kbd size="xs" color="mono" variant="solid" value="ctrl" />
            <e-kbd size="xs" color="mono" variant="solid" value="K" />
          </span>
        </span>
      </e-tooltip>
    </template>
  </div>
</template>
